import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Title, Tab, Field, Control, Button, Icon } from "rbx";
import { useMutation, useApolloClient } from "react-apollo";
import { Link, Route, useHistory, Switch } from "react-router-dom";

import {
  CREATE_EMPLOYEE_MUTATION,
  EMPLOYEE_PAGINATION_QUERY
} from "../../../graphql/Employees/Employees.graphql";

import IconLink from "../../../components/IconLink";
import { useLocalStorage } from "../../../utils/hooks";
import Personal from "./routes/Personal";
import {
  getUTCFormattedDate,
  unmaskCurrency,
  ourToast as toast
} from "../../../utils";
import Loading from "../../../components/Loading";

const INITIAL_STATE = {
  RecId: null,
  EmployeeId: null,
  LastName: "",
  FirstName: "",
  MiddleName: "",
  Active: true,
  Address1: null,
  Address2: null,
  City: null,
  State: null,
  ZipCode: null,
  Country: null,
  HomePhoneNo: null,
  CellPhoneNo: null,
  AlternatePhoneNo: null,
  EmergencyPhoneNo: null,
  EmergencyContact: null,
  EmergencyRelationship: null,
  Email1: null,
  Email2: null,
  Veteran: null,
  PayrollId: null,
  SocialSecurityNo: null,
  JobTitle: null,
  BirthDate: "",
  HireDate: "",
  TerminationDate: "",
  TerminationReasonRecIdRef: null,
  TerminationNote: null,
  RehireDate: "",
  PayrollNote: null,
  CompanyId: null,
  SupervisorRecIdRef: null,
  EthnicityRecIdRef: null,
  EmployeeTypeRecIdRef: null,
  ContactPreferenceRecIdRef: null,
  DriverFlag: null,
  MechanicFlag: null,
  DrugTest: null,
  HomeCompanyLocationId: null,
  ShopSteward: null,
  HourlyRate: "$0.00",
  DriverLicenseNo: null,
  DriverLicenseState: null
};

const NewEmployeePage = () => {
  const history = useHistory();
  const [inputs, setInputs, clearInputs] = useLocalStorage(
    "NEW_EMPLOYEE",
    INITIAL_STATE
  );
  const [editing, setEditing] = useState(true);
  const client = useApolloClient();
  const [createEmployee, res] = useMutation(CREATE_EMPLOYEE_MUTATION);

  const handleChange = e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const updates = { ...inputs };
    updates[e.target.name] = value;
    if (e.target.name === "HourlyRate") {
      // If it's a currency input
      const unmaskedValue = unmaskCurrency(e.target.value);
      updates[e.target.name] = unmaskedValue;
    } else if (e.target.name === "VacationDaysRenewalDate") {
      updates.PersonalDaysRenewalDate = value;
    } else if (e.target.name === "EmployeeTypeRecIdRef") {
      updates.DriverFlag = value === "30" || value === "34";
      updates.MechanicFlag = value === "32";
    }
    setInputs({ ...updates });
  };

  const handleSave = async goTo => {
    try {
      const obj = Object.keys(INITIAL_STATE).reduce(
        (acc, curr) =>
          inputs[curr] !== null ? { ...acc, [curr]: inputs[curr] } : acc,
        {}
      );

      if (String(inputs?.EmployeeTypeRecIdRef) !== "34") {
        obj.VacationDaysRenewalDate = inputs.VacationDaysRenewalDate
          ? getUTCFormattedDate(inputs.VacationDaysRenewalDate)
          : null;
        obj.PersonalDaysRenewalDate = inputs.PersonalDaysRenewalDate
          ? getUTCFormattedDate(inputs.PersonalDaysRenewalDate)
          : null;
        obj.TerminationDate = inputs.TerminationDate
          ? getUTCFormattedDate(inputs.TerminationDate)
          : null;
        obj.TerminationReasonRecIdRef = parseInt(
          inputs.TerminationReasonRecIdRef
        );
        obj.VacationDaysAllowedThisYear = parseInt(
          inputs.VacationDaysAllowedThisYear
        );
        obj.VacationDaysUsedThisYear = parseInt(
          inputs.VacationDaysUsedThisYear
        );
        obj.PersonalDaysAllowedThisYear = parseInt(
          inputs.PersonalDaysAllowedThisYear
        );
        obj.PersonalDaysUsedThisYear = parseInt(
          inputs.PersonalDaysUsedThisYear
        );
        obj.Weight = parseInt(inputs.Weight);
      }
      obj.ELDStatusRecIdRef =
        inputs.ELDStatusRecIdRef && inputs.DriverFlag
          ? parseInt(inputs.ELDStatusRecIdRef)
          : null;
      obj.TerminationDate =
        inputs.TerminationDate !== "" ? inputs.TerminationDate : null;

      const data = {
        ...obj,
        HireDate: obj.HireDate ? getUTCFormattedDate(obj.HireDate) : null,
        BirthDate: obj.BirthDate ? getUTCFormattedDate(obj.BirthDate) : null,
        RehireDate: obj.RehireDate ? getUTCFormattedDate(obj.RehireDate) : null,
        SupervisorRecIdRef: parseInt(inputs?.SupervisorRecIdRef),
        EthnicityRecIdRef: parseInt(inputs?.EthnicityRecIdRef),
        EmployeeTypeRecIdRef: parseInt(inputs?.EmployeeTypeRecIdRef),
        ContactPreferenceRecIdRef: parseInt(inputs?.ContactPreferenceRecIdRef),
        HourlyRate: parseFloat(unmaskCurrency(inputs?.HourlyRate)),
        TerminationReasonRecIdRef:
          inputs.TerminationReasonRecIdRef && obj.TerminationDate
            ? parseInt(inputs.TerminationReasonRecIdRef)
            : null,
        SeniorityDate: inputs.SeniorityDate
          ? getUTCFormattedDate(inputs.SeniorityDate)
          : null,
        LastWorkedDate: inputs.LastWorkedDate
          ? getUTCFormattedDate(inputs.LastWorkedDate)
          : null
      };
      const EmployeeInformation = await client.query({
        query: EMPLOYEE_PAGINATION_QUERY,
        variables: {
          where: { EmployeeId_startsWith: data.LastName.substr(0, 3) }
        },
        fetchPolicy: "network-only"
      });
      const EmployeeId = `${inputs.LastName.substr(0, 3).toUpperCase()}${(
        parseInt(
          EmployeeInformation?.data?.EmployeesConnection.aggregate.count
        ) + 1
      )
        .toString()
        .padStart(3, "0")}`;
      const create = await createEmployee({
        variables: {
          data: {
            ...data,
            EmployeeId
          }
        }
      });
      clearInputs();
      if (!goTo && create?.data?.createEmployee) {
        const { RecId } = create?.data?.createEmployee;
        toast.success("Employee created successfully.");
        history.push(`/hr/employees/${RecId}/personal`);
      } else if (goTo && create?.data?.createEmployee) {
        const { RecId } = create?.data?.createEmployee;
        toast.success("Employee created successfully.");
        history.push(`/hr/employees/${RecId}/${goTo}`);
      }
      setEditing(false);
    } catch (err) {
      console.log(err.message);
    }
  };

  if (res?.loading) return <Loading />;

  const getActionButtons = () => (
    <Field kind="group">
      <Control>
        <Button
          color="success"
          disabled={!editing}
          size="small"
          onClick={() => handleSave(false)}
        >
          <Icon>
            <FontAwesomeIcon icon="save" />
          </Icon>
          <span>Save</span>
        </Button>
      </Control>
    </Field>
  );
  return (
    <React.Fragment>
      <header className="page-head">
        <div className="page-head-start">
          <Title color="primary" size={3}>
            {inputs?.FirstName || inputs?.LastName
              ? `${inputs?.FirstName} ${inputs?.LastName}`
              : "Add Employee"}
          </Title>
          <div className="page-head-start--subheader">
            <IconLink
              href={`mailto:${inputs?.Email1}`}
              icon="envelope"
              label={inputs?.Email1}
            />
            <IconLink
              href={`tel:${inputs?.HomePhoneNo}`}
              icon="phone-alt"
              label={inputs?.HomePhoneNo}
            />
          </div>
        </div>
        <div className="page-head-end">{getActionButtons()}</div>
      </header>
      <Tab.Group kind="boxed">
        <Tab active as={Link} tabIndex="-1" to="personal">
          <span>Personal</span>
        </Tab>
        <Tab
          as={Button}
          style={{ cursor: "pointer" }}
          onClick={() => handleSave("payroll")}
        >
          <span>Payroll</span>
        </Tab>
        {/* SPARE DRIVERS DON'T NEED VACATION TIME */}
        {String(inputs?.EmployeeTypeRecIdRef) !== "34" && (
          <Tab
            as={Button}
            style={{ cursor: "pointer" }}
            onClick={() => handleSave("vacation")}
          >
            <span>Vacation</span>
          </Tab>
        )}
        <Tab
          as={Button}
          style={{ cursor: "pointer" }}
          onClick={() => handleSave("certifications")}
        >
          <span>Certifications</span>
        </Tab>
        <Tab
          as={Button}
          style={{ cursor: "pointer" }}
          onClick={() => handleSave("notes")}
        >
          <span>Notes</span>
        </Tab>
      </Tab.Group>
      <Switch>
        <Route path="/hr/employees/new">
          <Personal
            editing={editing}
            employee={inputs}
            onChange={handleChange}
          />
        </Route>
      </Switch>
    </React.Fragment>
  );
};
NewEmployeePage.propTypes = {};

export default NewEmployeePage;
